import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { AcademyAutocomplete } from '@/components/ui/AcademyAutocomplete';
import { ProfessionAutocomplete } from '@/components/ui/ProfessionAutocomplete';
import { InterestsStep } from '@/components/onboarding/steps/HobbiesStep';
import { TechniquesStep } from '@/components/onboarding/steps/TechniquesStep';
import { SocialStep } from '@/components/onboarding/steps/SocialStep';

export type WizardStepType = 'academy' | 'profession' | 'interests' | 'techniques' | 'social';

interface SingleStepWizardModalProps {
  open: boolean;
  type: WizardStepType | null;
  onClose: () => void;
  onSaved: () => void;
}

// ─── Shared footer buttons ────────────────────────────────────────────────────
const Footer = ({
  onCancel,
  onSave,
  disabled,
  saving,
  saveLabel,
  cancelLabel,
}: {
  onCancel: () => void;
  onSave: () => void;
  disabled: boolean;
  saving: boolean;
  saveLabel: string;
  cancelLabel: string;
}) => (
  <div className="flex gap-2 mt-5">
    <button
      onClick={onCancel}
      className="flex-1 h-11 rounded-full bg-[#F0F0F0] text-[#1a1a1a] text-[14px] font-semibold transition-opacity active:opacity-70"
    >
      {cancelLabel}
    </button>
    <button
      onClick={onSave}
      disabled={disabled || saving}
      className="flex-1 h-11 rounded-full bg-sossiety-red text-white text-[14px] font-semibold disabled:opacity-40 transition-opacity active:opacity-80"
    >
      {saving ? '···' : saveLabel}
    </button>
  </div>
);

// ─── Academy step ─────────────────────────────────────────────────────────────
const AcademyStep = ({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) => {
  const { user } = useAuth();
  const { t } = useI18n();
  const { toast } = useToast();
  const [academy, setAcademy] = useState<{ id: string; name: string } | string>('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);

  const hasSelection = academy && typeof academy !== 'string' && (academy as any).id;

  const handleSave = async () => {
    if (!user || !hasSelection) return;
    setSaving(true);
    setSaveError(false);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ main_academy_id: (academy as any).id })
        .eq('id', user.id);
      if (error) throw error;
      toast({ title: t('wizard.saved') });
      onSaved();
      onClose();
    } catch {
      setSaveError(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <AcademyAutocomplete value={academy} onChange={setAcademy} theme="light" />
      {saveError && (
        <p className="text-[12px] text-red-500 mt-2">{t('common.error')}</p>
      )}
      <Footer
        onCancel={onClose}
        onSave={handleSave}
        disabled={!hasSelection}
        saving={saving}
        saveLabel={t('common.save')}
        cancelLabel={t('common.cancel')}
      />
    </>
  );
};

// ─── Profession step ──────────────────────────────────────────────────────────
const ProfessionStep = ({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) => {
  const { user } = useAuth();
  const { t } = useI18n();
  const { toast } = useToast();
  const [profession, setProfession] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);

  const handleSave = async () => {
    if (!user || !profession.trim()) return;
    setSaving(true);
    setSaveError(false);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ profession: profession.trim() })
        .eq('id', user.id);
      if (error) throw error;
      toast({ title: t('wizard.saved') });
      onSaved();
      onClose();
    } catch {
      setSaveError(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <ProfessionAutocomplete
        value={profession}
        onChange={setProfession}
        theme="light"
        placeholder={t('onboarding.profession.profession_placeholder')}
      />
      {saveError && (
        <p className="text-[12px] text-red-500 mt-2">{t('common.error')}</p>
      )}
      <Footer
        onCancel={onClose}
        onSave={handleSave}
        disabled={!profession.trim()}
        saving={saving}
        saveLabel={t('common.save')}
        cancelLabel={t('common.cancel')}
      />
    </>
  );
};

// ─── Interests step ───────────────────────────────────────────────────────────
const InterestsStepWrapper = ({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) => {
  const { user } = useAuth();
  const { t } = useI18n();
  const { toast } = useToast();
  const [selected, setSelected] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);

  const handleSave = async () => {
    if (!user || selected.length === 0) return;
    setSaving(true);
    setSaveError(false);
    try {
      // Resolve hobby names → IDs
      const { data: hobbies, error: fetchErr } = await supabase
        .from('hobbies')
        .select('id, name')
        .in('name', selected);
      if (fetchErr) throw fetchErr;

      // Replace existing interests
      await supabase.from('profile_hobbies').delete().eq('profile_id', user.id);

      if (hobbies && hobbies.length > 0) {
        const { error: insertErr } = await supabase
          .from('profile_hobbies')
          .insert(hobbies.map(h => ({ profile_id: user.id, hobby_id: h.id })));
        if (insertErr) throw insertErr;
      }

      toast({ title: t('wizard.saved') });
      onSaved();
      onClose();
    } catch {
      setSaveError(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="max-h-[50vh] overflow-y-auto">
        <InterestsStep selected={selected} setSelected={setSelected} />
      </div>
      {saveError && (
        <p className="text-[12px] text-red-500 mt-2">{t('common.error')}</p>
      )}
      <Footer
        onCancel={onClose}
        onSave={handleSave}
        disabled={selected.length === 0}
        saving={saving}
        saveLabel={t('common.save')}
        cancelLabel={t('common.cancel')}
      />
    </>
  );
};

// ─── Techniques step ──────────────────────────────────────────────────────────
const TechniquesStepWrapper = ({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) => {
  const { user } = useAuth();
  const { t } = useI18n();
  const { toast } = useToast();
  const [categoryIndex, setCategoryIndex] = useState(0);
  const [selected, setSelected] = useState<Record<string, string[]>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);

  const hasSelection = Object.values(selected).some(arr => arr.length > 0);

  const handleSave = async () => {
    if (!user || !hasSelection) return;
    setSaving(true);
    setSaveError(false);
    try {
      // Resolve technique names → IDs
      const allNames = Object.values(selected).flat();
      const { data: techniques, error: fetchErr } = await supabase
        .from('techniques')
        .select('id, name')
        .in('name', allNames);
      if (fetchErr) throw fetchErr;

      // Replace existing favorite techniques
      await supabase.from('profile_favorite_techniques').delete().eq('profile_id', user.id);

      if (techniques && techniques.length > 0) {
        const { error: insertErr } = await supabase
          .from('profile_favorite_techniques')
          .insert(techniques.map(t => ({ profile_id: user.id, technique_id: t.id })));
        if (insertErr) throw insertErr;
      }

      toast({ title: t('wizard.saved') });
      onSaved();
      onClose();
    } catch {
      setSaveError(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="max-h-[55vh] overflow-y-auto">
        <TechniquesStep
          categoryIndex={categoryIndex}
          setCategoryIndex={setCategoryIndex}
          selected={selected}
          setSelected={setSelected}
        />
      </div>
      {saveError && (
        <p className="text-[12px] text-red-500 mt-2">{t('common.error')}</p>
      )}
      <Footer
        onCancel={onClose}
        onSave={handleSave}
        disabled={!hasSelection}
        saving={saving}
        saveLabel={t('common.save')}
        cancelLabel={t('common.cancel')}
      />
    </>
  );
};

// ─── Social step ──────────────────────────────────────────────────────────────
const EMPTY_SOCIAL = { instagram: '', x: '', facebook: '', youtube: '', website: '' };

const SocialStepWrapper = ({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) => {
  const { user } = useAuth();
  const { t } = useI18n();
  const { toast } = useToast();
  const [social, setSocial] = useState(EMPTY_SOCIAL);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);

  const hasValue = Object.values(social).some(v => v.trim() !== '');

  const handleSave = async () => {
    if (!user || !hasValue) return;
    setSaving(true);
    setSaveError(false);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          instagram: social.instagram.trim() || null,
          x: social.x.trim() || null,
          facebook: social.facebook.trim() || null,
          youtube: social.youtube.trim() || null,
          website: social.website.trim() || null,
        })
        .eq('id', user.id);
      if (error) throw error;
      toast({ title: t('wizard.saved') });
      onSaved();
      onClose();
    } catch {
      setSaveError(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="max-h-[55vh] overflow-y-auto">
        <SocialStep selected={social} setSelected={setSocial} />
      </div>
      {saveError && (
        <p className="text-[12px] text-red-500 mt-2">{t('common.error')}</p>
      )}
      <Footer
        onCancel={onClose}
        onSave={handleSave}
        disabled={!hasValue}
        saving={saving}
        saveLabel={t('common.save')}
        cancelLabel={t('common.cancel')}
      />
    </>
  );
};

// ─── Modal titles per step type ───────────────────────────────────────────────
function useTitleKey(type: WizardStepType | null) {
  const { t } = useI18n();
  if (type === 'academy') return t('discovery.cta.add_academy_title');
  if (type === 'profession') return t('discovery.cta.add_profession_title');
  if (type === 'interests') return t('discovery.cta.pick_interests_title');
  if (type === 'techniques') return t('discovery.cta.add_techniques_title');
  if (type === 'social') return t('discovery.cta.add_social_title');
  return '';
}

// ─── Main modal ───────────────────────────────────────────────────────────────
export const SingleStepWizardModal: React.FC<SingleStepWizardModalProps> = ({
  open,
  type,
  onClose,
  onSaved,
}) => {
  const title = useTitleKey(type);

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="w-[92vw] max-w-sm rounded-2xl bg-white p-6 shadow-xl">
        <DialogHeader>
          <DialogTitle className="text-[17px] font-semibold text-[#1a1a1a] leading-tight">
            {title}
          </DialogTitle>
        </DialogHeader>

        <div className="mt-2">
          {type === 'academy' && <AcademyStep onClose={onClose} onSaved={onSaved} />}
          {type === 'profession' && <ProfessionStep onClose={onClose} onSaved={onSaved} />}
          {type === 'interests' && <InterestsStepWrapper onClose={onClose} onSaved={onSaved} />}
          {type === 'techniques' && <TechniquesStepWrapper onClose={onClose} onSaved={onSaved} />}
          {type === 'social' && <SocialStepWrapper onClose={onClose} onSaved={onSaved} />}
        </div>
      </DialogContent>
    </Dialog>
  );
};
